import React from 'react';
import { AdicionalCategoria, AdicionalItem } from '../../types';
import { formatCurrency } from '../../constants';

interface AdminAdicionaisProps {
  adicionaisCats: AdicionalCategoria[];
  isUpdating: boolean;
  setIsEditingAdicionalCat: (open: boolean) => void;
  setCurrentAdicionalCat: (cat: Partial<AdicionalCategoria>) => void;
  setIsEditingAdicionalItem: (open: boolean) => void;
  setCurrentAdicionalItem: (item: Partial<AdicionalItem>) => void;
  handleDeleteAdicionalCat: (id: string) => void;
  handleDeleteAdicionalItem: (id: string) => void;
  isDemo?: boolean;
}

export const AdminAdicionais: React.FC<AdminAdicionaisProps> = ({
  adicionaisCats,
  isUpdating,
  setIsEditingAdicionalCat,
  setCurrentAdicionalCat,
  setIsEditingAdicionalItem,
  setCurrentAdicionalItem,
  handleDeleteAdicionalCat,
  handleDeleteAdicionalItem,
  isDemo = false
}) => {
  return (
    <div className="p-4 md:p-10 animate-fade-in space-y-6 md:space-y-10 pb-20">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">Complementos</h3>
          <p className="text-slate-400 text-xs md:text-sm font-bold mt-1">Opcionais e grupos de adicionais para seus itens.</p>
        </div>
        <button 
          onClick={() => { if(isDemo) return alert("Modo demonstração"); setCurrentAdicionalCat({ nome: '', minimo: 0, maximo: 1, obrigatorio: false, ativo: true }); setIsEditingAdicionalCat(true); }} 
          className={`w-full sm:w-auto bg-orange-600 text-white px-6 py-4 sm:py-3 rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 shadow-xl ${isDemo ? 'opacity-40 cursor-not-allowed' : ''}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4"></path></svg>
          Novo Grupo
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        {adicionaisCats.map(cat => (
          <div key={cat.id} className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col">
            <div className="p-6 md:p-8 border-b border-slate-100 bg-slate-50/50 flex justify-between items-start gap-4">
              <div className="min-w-0">
                <h4 className="font-black text-slate-900 text-lg flex flex-wrap items-center gap-2">
                  {cat.nome}
                  {cat.obrigatorio && <span className="text-[8px] bg-slate-900 text-white px-2 py-0.5 rounded font-black uppercase tracking-widest">Obrigatório</span>}
                </h4>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                  Min: {cat.minimo} • Máx: {cat.maximo}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => { if(isDemo) return alert("Modo demonstração"); setCurrentAdicionalCat(cat); setIsEditingAdicionalCat(true); }} className="p-2 text-slate-400 hover:text-slate-900 transition-all bg-white rounded-xl border border-slate-100"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg></button>
                <button onClick={() => { if(isDemo) return alert("Modo demonstração"); if(confirm('Excluir grupo?')) handleDeleteAdicionalCat(cat.id); }} className="p-2 text-slate-400 hover:text-red-500 transition-all bg-white rounded-xl border border-slate-100"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
              </div>
            </div>

            <div className="p-6 md:p-8 flex-1 space-y-5">
              <div className="flex justify-between items-center">
                <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Opções do Grupo</h5>
                <button onClick={() => { if(isDemo) return alert("Modo demonstração"); setCurrentAdicionalItem({ categoria_adicional_id: cat.id, nome: '', preco: 0, ativo: true }); setIsEditingAdicionalItem(true); }} className="text-[10px] font-black text-orange-600 uppercase tracking-widest hover:underline">+ Nova Opção</button>
              </div>

              <div className="space-y-3">
                {cat.itens?.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 group">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${item.ativo ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                      <span className="font-bold text-slate-700 text-sm truncate">{item.nome}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-4">
                      <span className="text-xs font-black text-slate-900">{item.preco > 0 ? formatCurrency(item.preco) : 'Grátis'}</span>
                      <div className="flex sm:opacity-0 group-hover:opacity-100 transition-all gap-1">
                        <button onClick={() => { if(isDemo) return alert("Modo demonstração"); setCurrentAdicionalItem(item); setIsEditingAdicionalItem(true); }} className="p-1.5 text-slate-400 hover:text-orange-600"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg></button>
                        <button onClick={() => { if(isDemo) return alert("Modo demonstração"); if(confirm('Excluir?')) handleDeleteAdicionalItem(item.id); }} className="p-1.5 text-slate-400 hover:text-red-500"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                      </div>
                    </div>
                  </div>
                ))}
                {(!cat.itens || cat.itens.length === 0) && (
                    <div className="py-8 text-center border-2 border-dashed border-slate-100 rounded-3xl opacity-40"><p className="text-[10px] font-black uppercase tracking-widest">Nenhuma opção cadastrada</p></div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};